/**
 * Hook: "what changed since last export?"
 *
 * Calls /video-export/diff/:conversationId on demand. Used by:
 *   - VideoPublishButton dropdown to show pre-export change badge
 *   - Per-share-link card to show "outdated" indicator
 *
 * Cheap to call (one DB query for assembly metadata + one for child slides),
 * so we don't aggressively cache. Caller decides when to refresh — typically
 * on dropdown open and after operations that mutate state (slide edit, audio
 * change, share link update).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { videoExportAPI, type ExportDiff } from '@/services/api';

interface UseExportDiffResult {
  diff: ExportDiff | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useExportDiff(conversationId: string | undefined): UseExportDiffResult {
  const [diff, setDiff] = useState<ExportDiff | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inFlightRef = useRef(false);

  const refetch = useCallback(async () => {
    if (!conversationId || inFlightRef.current) return;
    inFlightRef.current = true;
    setIsLoading(true);
    setError(null);
    try {
      const data = await videoExportAPI.getDiff(conversationId);
      setDiff(data);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load export diff');
    } finally {
      inFlightRef.current = false;
      setIsLoading(false);
    }
  }, [conversationId]);

  // Initial fetch when the conversation changes.
  useEffect(() => {
    if (!conversationId) return;
    void refetch();
  }, [conversationId, refetch]);

  return { diff, isLoading, error, refetch };
}
